// Location API Service for Frontend
// This service communicates with Cloudflare Functions API endpoints for location management

export interface LocationInput {
  name: string;
  
  // Monthly Base ADR (Average Daily Rate) in EUR
  adr_jan?: number;
  adr_feb?: number;
  adr_mar?: number;
  adr_apr?: number;
  adr_may?: number;
  adr_jun?: number;
  adr_jul?: number;
  adr_aug?: number;
  adr_sep?: number;
  adr_oct?: number;
  adr_nov?: number;
  adr_dec?: number;
  
  // Monthly Base Occupancy (%)
  occupancy_jan?: number;
  occupancy_feb?: number;
  occupancy_mar?: number;
  occupancy_apr?: number;
  occupancy_may?: number;
  occupancy_jun?: number;
  occupancy_jul?: number;
  occupancy_aug?: number;
  occupancy_sep?: number;
  occupancy_oct?: number;
  occupancy_nov?: number;
  occupancy_dec?: number;
}

export interface Location {
  id: string;
  name: string;
  
  // Monthly Base ADR (Average Daily Rate) in EUR
  adr_jan: number;
  adr_feb: number;
  adr_mar: number;
  adr_apr: number;
  adr_may: number;
  adr_jun: number;
  adr_jul: number;
  adr_aug: number;
  adr_sep: number;
  adr_oct: number;
  adr_nov: number;
  adr_dec: number;
  
  // Monthly Base Occupancy (%)
  occupancy_jan: number;
  occupancy_feb: number;
  occupancy_mar: number;
  occupancy_apr: number;
  occupancy_may: number;
  occupancy_jun: number;
  occupancy_jul: number;
  occupancy_aug: number;
  occupancy_sep: number;
  occupancy_oct: number;
  occupancy_nov: number;
  occupancy_dec: number;
  
  created_at: string;
  updated_at: string;
}

export interface LocationWithSelection extends Location {
  isSelected: boolean;
}

// API Functions
export const locationAPI = {
  // Get all locations
  async getLocations(): Promise<Location[]> {
    try {
      // Use relative URL to work with any deployment
      const response = await fetch('/api/locations');
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      console.error('Error fetching locations:', error);
      throw error;
    }
  },

  // Get a single location by ID
  async getLocation(id: string): Promise<Location> {
    try {
      const response = await fetch(`/api/locations/${id}`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      console.error('Error fetching location:', error);
      throw error;
    }
  },

  // Create a new location
  async createLocation(locationData: LocationInput): Promise<Location> {
    try {
      const response = await fetch('/api/locations', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(locationData),
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      console.error('Error creating location:', error);
      throw error;
    }
  },

  // Update an existing location
  async updateLocation(id: string, locationData: Partial<LocationInput>): Promise<Location> {
    try {
      const response = await fetch(`/api/locations/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(locationData),
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      console.error('Error updating location:', error);
      throw error;
    }
  },

  // Delete a location
  async deleteLocation(id: string): Promise<void> {
    try {
      const response = await fetch(`/api/locations/${id}`, {
        method: 'DELETE',
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
    } catch (error) {
      console.error('Error deleting location:', error);
      throw error;
    }
  },
};
